import { AvatarThemeOptions } from "./types";

function getInitials(name: string): string {
  const match = name.match(
    /^\s*([A-Za-z])[^\s]*\s+(?:[A-Za-z]+?\s+)?([A-Za-z])[^\s]*/
  );
  if (match) {
    const [, first, last] = match;
    return (first + last).toUpperCase();
  }

  // fallback for single name
  return name[0]?.toUpperCase() ?? "";
}

function getThemeColors(options: AvatarThemeOptions = {}) {
  const { name, useNameBasedColor = false, theme = "dark" } = options;

  if (useNameBasedColor && name) {
    return generateThemeColorsFromName(name, theme);
  }

  return getRandomThemeColors(theme);
}

function generateThemeColorsFromName(
  name: string,
  theme: "dark" | "light"
): { primary: string; contrast: string } {
  const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);

  const baseHue = hash % 360;
  const saturation = 70 + (hash % 20); // Slight variation

  // Base color
  const primaryLightness =
    theme === "dark"
      ? 30 + (hash % 10) // 30–39%
      : 65 + (hash % 10); // 65–74%

  // Contrast shade of same hue (not complementary hue)
  const contrastLightness =
    theme === "dark"
      ? primaryLightness + 30 // lighter for text
      : primaryLightness - 30; // darker for text

  const clampedContrastLightness = Math.max(
    10,
    Math.min(90, contrastLightness)
  );

  const primary = hslToHex(baseHue, saturation, primaryLightness);
  const contrast = hslToHex(baseHue, saturation, clampedContrastLightness);

  return { primary, contrast };
}

function getRandomThemeColors(theme: "dark" | "light" = "dark") {
  const hue = Math.floor(Math.random() * 360);
  const saturation = 70 + Math.random() * 30;

  const baseLightness =
    theme === "dark"
      ? 15 + Math.random() * 10 // 15–25%
      : 65 + Math.random() * 10; // 65–75%

  const contrastLightness =
    theme === "dark"
      ? baseLightness + 15 // 30–40%
      : baseLightness - 30; // 35–45%

  const primary = hslToHex(hue, saturation, baseLightness);
  const contrast = hslToHex(hue, saturation, contrastLightness);

  return {
    primary,
    contrast,
  };
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let [r, g, b] = [0, 0, 0];

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export { getInitials, getThemeColors };
